import React, { useEffect, useState } from "react";
import "./Scheduling.css";
import { SchedulingMetricsModal } from "./SchedulingMetricsModal";
import axios from "axios";

function Scheduling({ projectData }) {
  // ----- State for date items (First, Most Recent, Next Onsite) -----
  const [dateItems, setDateItems] = useState([
    { label: "First Onsite", date: "N/A" },
    { label: "Most Recent Onsite", date: "N/A" },
    { label: "Next Onsite", date: "N/A" },
  ]);

  // ----- State for scheduling metrics -----
  const [metrics, setMetrics] = useState([
    { label: "Mobs Used/Contracted", value: "N/A", type: "mobs", used: 0, total: 0 },
    { label: "Days Used/Estimated", value: "N/A", type: "days", used: 0, total: 0 },
    { label: "Trips Used/Estimated", value: "N/A", type: "trips", used: 0, total: 0 },
  ]);

  // ----- Modal State -----
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    metricType: null,
    currentValues: null,
  });

  const [loading, setLoading] = useState(false);

  // Helper to format date to MM/DD/YYYY
  const formatUSDate = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 1) Fetch Scheduling Metrics
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchSchedulingMetrics = async () => {
    if (!projectData?.opportunityNo) return;

    try {
      const response = await axios.get(
        `https://projects-dev.nettlescs.com:5000/api/schedules/${projectData.opportunityNo}`
      );

      if (response.data.status === "success" && response.data.data) {
        const data = response.data.data;
        setMetrics([
          {
            label: "Mobs Used/Contracted",
            value: `${data.mobs_used}/${data.mobs_contracted}`,
            type: "mobs",
            used: data.mobs_used,
            total: data.mobs_contracted,
          },
          {
            label: "Days Used/Estimated",
            value: `${data.days_used}/${data.days_estimated}`,
            type: "days",
            used: data.days_used,
            total: data.days_estimated,
          },
          {
            label: "Trips Used/Estimated",
            value: `${data.trip_used}/${data.trip_estimated}`,
            type: "trips",
            used: data.trip_used,
            total: data.trip_estimated,
          },
        ]);
      } else {
        setMetrics([
          { label: "Mobs Used/Contracted", value: "N/A", type: "mobs", used: 0, total: 0 },
          { label: "Days Used/Estimated", value: "N/A", type: "days", used: 0, total: 0 },
          { label: "Trips Used/Estimated", value: "N/A", type: "trips", used: 0, total: 0 },
        ]);
      }
    } catch (error) {
      console.error("Error fetching scheduling metrics:", error);
      setMetrics([
        { label: "Mobs Used/Contracted", value: "N/A", type: "mobs", used: 0, total: 0 },
        { label: "Days Used/Estimated", value: "N/A", type: "days", used: 0, total: 0 },
        { label: "Trips Used/Estimated", value: "N/A", type: "trips", used: 0, total: 0 },
      ]);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 2) Fetch Onsite Dates from New API
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchOnsiteDatesFromAPI = async () => {
    if (!projectData?.opportunityNo) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `https://projects-dev.nettlescs.com:5000/api/opportunity-events/${projectData.opportunityNo}`
      );

      if (response.data) {
        const { first_onsite, most_recent_onsite, next_onsite } = response.data;

        setDateItems([
          { label: "First Onsite", date: first_onsite ? formatUSDate(new Date(first_onsite)) : "N/A" },
          { label: "Most Recent Onsite", date: most_recent_onsite ? formatUSDate(new Date(most_recent_onsite)) : "N/A" },
          { label: "Next Onsite", date: next_onsite ? formatUSDate(new Date(next_onsite)) : "N/A" },
        ]);
      } else {
        setDateItems([
          { label: "First Onsite", date: "N/A" },
          { label: "Most Recent Onsite", date: "N/A" },
          { label: "Next Onsite", date: "N/A" },
        ]);
      }
    } catch (error) {
      console.error("Error fetching onsite dates:", error);
      setDateItems([
        { label: "First Onsite", date: "N/A" },
        { label: "Most Recent Onsite", date: "N/A" },
        { label: "Next Onsite", date: "N/A" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 3) Effects: Fetch Metrics and Onsite Dates
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchSchedulingMetrics();
    fetchOnsiteDatesFromAPI();
  }, [projectData]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4) Handlers
  // ─────────────────────────────────────────────────────────────────────────────
  const handleDateClick = (item) => {
    if (item.date !== "N/A") {
      window.location.href = "ms-outlook://calendar";
    }
  };



  const handleMetricClick = (metric) => {
    setModalConfig({
      isOpen: true,
      metricType: metric.type,
      currentValues: { used: metric.used, total: metric.total },
    });
  };

  const closeModal = () => {
    setModalConfig({
      isOpen: false,
      metricType: null,
      currentValues: null,
    });
  };

  const getMetricValueColor = (used, total) => {
    if (used === 0 && total === 0) return {};
    return { color: used <= total ? "#34d399" : "#b91c1c" };
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5) Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <section className="scheduling">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "1rem",
        }}
      >
        <h2 className="scheduling-title">Scheduling</h2>
        {loading && (
          <div className="loader" style={{ marginBottom: "10px" }} />
        )}
      </div>

      {/* Dates & Metrics Grid */}
      <div className="schedule-grid">
        {/* Date Items */}
        {dateItems.map((item, index) => (
          <div
            key={index}
            className="schedule-item"
            onClick={() => handleDateClick(item)}
            style={{ cursor: item.date !== "N/A" ? "pointer" : "default" }}
          >
            <div className="item-label">{item.label}</div>
            <div className="item-value">{item.date}</div>
          </div>
        ))}

        {/* Metrics Items */}
        {metrics.map((metric, index) => (
          <div
            key={`metric-${index}`}
            className="schedule-item cursor-pointer hover:bg-gray-50"
            onClick={() => handleMetricClick(metric)}
          >
            <div className="item-label">{metric.label}</div>
            <div
              className="item-value"
              style={getMetricValueColor(metric.used, metric.total)}
            >
              {metric.value}
            </div>
          </div>
        ))}
      </div>

      {/* Modal for editing metrics */}
      <SchedulingMetricsModal
        isOpen={modalConfig.isOpen}
        onClose={(success) => {
          closeModal();
          if (success) {
            fetchSchedulingMetrics();
          }
        }}
        metricType={modalConfig.metricType}
        currentValues={modalConfig.currentValues}
        projectData={projectData}
      />
    </section>
  );
}

export default Scheduling;
